import { GzDialogEvents } from 'apps/webcomponents/src/ui/gzDialog/component/GzDialog';
import { DOMEventDispatcher } from 'apps/webcomponents/src/utils/event.utils';
import { LitElement, html } from 'lit';
import { property, state } from 'lit/decorators.js';
import { RestoreFormData, RestoreType } from '../../../computersAndVirtualMachines/types';
import { styles } from '../view/styles';
import { WEB_COMPONENT_GZ_RADIO, WEB_COMPONENT_GZ_RADIO_GROUP } from '../../../../webcomponents.constants';
import { GzRadioGroupWebComponent, GzRadioWebComponent } from '../../../../ui/gzRadio';
import { GzTextWebComponent } from '../../../../ui/gzText';
import { ElementClass, elementClassListRemove } from '../../../../utils/htmlElement.utils';

export class QuarantineComputersAndVirtualMachinesRestoreForm extends LitElement {
  @property({ type: Function })
  public translateFn: Function;

  @state()
  private translations = {
    restoreOriginal: null,
    restoreToFolder: null,
    disableOptionInfo: null,
    proceed: null,
    cancel: null,
  };

  @state()
  private textAreaDisabled = true;

  @state()
  public formData: RestoreFormData = {
    restoreType: RestoreType.ORIGINAL_LOCATION,
    restorePath: '',
    isValid: true,
  };

  @state()
  public itemsFromArchive = false;

  static get styles() {
    return styles;
  }

  protected firstUpdated(changedProperties): void {
    super.update(changedProperties);
    this.setupTranslations(this.translateFn);
  }

  protected updated(changedProperties): void {
    if (changedProperties.has('itemsFromArchive')) {
      if (this.itemsFromArchive) {
        this.updateComplete.then(() => {
          if (!this.shadowRoot) {
            return;
          }
          const radioInfoText = this.shadowRoot.querySelector('#disableOptionInfo') as GzTextWebComponent;
          elementClassListRemove(radioInfoText, ElementClass.HIDDEN);
          const radioGroup = this.shadowRoot.querySelector(WEB_COMPONENT_GZ_RADIO_GROUP) as GzRadioGroupWebComponent;
          radioGroup?.setEnabled(true, false);
          const radios = Array.from<GzRadioWebComponent>(this.shadowRoot.querySelectorAll(WEB_COMPONENT_GZ_RADIO));
          radios.forEach(radio => {
            if (String(radio.value) === String(RestoreType.ORIGINAL_LOCATION)) {
              radio.disabled = true;
            }
          });
          radioGroup.selectOption(String(RestoreType.FOLDER));
        });
      }
    }
  }

  private setupTranslations(translateFn): void {
    this.translations = {
      restoreToFolder: translateFn('Restore to this folder'),
      restoreOriginal: translateFn('Restore to the original location'),
      disableOptionInfo: translateFn('At least one quarantined file is located inside an archive and cannot be restored to its original location. To continue, deselect these files or restore all to a specific folder.'),
      proceed: translateFn('Save#preserve state'),
      cancel: translateFn('cancel'),
    };
  }

  private radioSelectionChange(event): void {
    this.formData = {
      ...this.formData,
      restoreType: Number(event.detail?.selectedValue),
    };

    this.validate();
  }

  private textAreaValueChange({ detail }): void {
    const value = detail.sourceEvent;

    this.formData = {
      ...this.formData,
      restorePath: value,
    };

    this.validate();
  }

  private validate(): void {
    this.textAreaDisabled = this.formData.restoreType !== 1;

    const formValid = !!(this.textAreaDisabled || this.formData.restorePath?.length);

    this.formData = {
      ...this.formData,
      isValid: formValid,
    };
  }

  private save(): void {
    if (this.formData.isValid) {
      DOMEventDispatcher.dispatchGlobalEvent(this, GzDialogEvents.CONFIRM, this.formData);
    }
  }

  private cancel(): void {
    DOMEventDispatcher.dispatchGlobalEvent(this, GzDialogEvents.CANCEL);
  }

  render() {
    return html`
      <form class="ml-5 mt-5">
        <gz-radio-group
          id="restoreType"
          selectedOption=${RestoreType.ORIGINAL_LOCATION}
          @radioSelectionChanged=${this.radioSelectionChange}
        >
          <gz-radio label="${this.translations.restoreOriginal}" value=${RestoreType.ORIGINAL_LOCATION}></gz-radio>
          <gz-text fontSize="12" class="pt-1 pb-2 hidden" id="disableOptionInfo">${this.translations.disableOptionInfo}</gz-text>
          <gz-radio label="${this.translations.restoreToFolder}" value=${RestoreType.FOLDER}></gz-radio>
        </gz-radio-group>

        <gz-textarea
          inputWidth="400"
          class="ml-3"
          placeholder="Specific folder paths"
          rows=${6}
          ?disabled=${this.textAreaDisabled}
          @change=${this.textAreaValueChange}
        >
        </gz-textarea>
      </form>

      <div class="footer__container">
        <gz-button data-confirm ?disabled=${!this.formData.isValid} @click=${this.save}>
          ${this.translations.proceed}
        </gz-button>
        <gz-button data-cancel type="outline" color="secondary" @click=${this.cancel}>
          ${this.translations.cancel}
        </gz-button>
      </div>
    `;
  }
}
